import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getWalletsThunk } from '@/core/services/Wallets/WalletSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Cryptocurrency } from '@/components/UI/Cryptocurrency'
import { Loading } from '@/components/UI/Loading'
import { Title } from '@/components/UI/Title'

import { calculateTotalBalance } from '@/utils/calculateTotalBalance'
import { formatNumber } from '@/utils/formatNumber'
import { getSymbolsHierarchy } from '@/utils/getSymbolsHierarchy'

import { walletsSelector } from '../../../core/services/Wallets/WalletSelector'
import './Home.scss'

const cnHome = cn('Home')

export const Home: FC = () => {
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()
  const { t } = useTranslation()

  const wallets = useAppSelector(walletsSelector)

  const [symbols, setSymbols] = useState<string[]>([])
  const [isLoading, setLoading] = useState<boolean>(true)
  const [balance, setBalance] = useState<string>('0')

  useEffect(() => {
    getWallets()
  }, [])

  useEffect(() => {
    if (wallets.length) {
      const res = getSymbolsHierarchy(wallets)

      setSymbols(res)

      const globalBalance = calculateTotalBalance(wallets)

      setBalance(formatNumber(globalBalance, 2))
    }
  }, [wallets])

  const getWallets = async () => {
    try {
      await dispatch(getWalletsThunk()).unwrap()
      setLoading(false)
    } catch (error) {
      setLoading(false)
      handleServerError(error)
    }
  }

  return (
    <div className={cnHome()}>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <>
          <Title>{t('home-your-cryptocurrencies')}</Title>

          <div className={cnHome('top')}>
            <div className={cnHome('item')}>
              <div className={cnHome('value')}>{balance} USD</div>
              <div className={cnHome('key')}>{t('home-total-balance')}</div>
            </div>

            {symbols.length > 0 && (
              <div className={cnHome('item')}>
                <div className={cnHome('value')}>
                  {symbols.length}

                  <div className={cnHome('symbols')}>
                    {symbols.map((symbol, i) => (
                      <div
                        key={i}
                        className={cnHome(
                          'symbol',
                          `icon-${symbol.toUpperCase()} ${symbol.toUpperCase()}-color-bg`,
                        )}
                      ></div>
                    ))}
                  </div>
                </div>
                <div className={cnHome('key')}>{t('home-assets')}</div>
              </div>
            )}
          </div>

          <div className={cnHome('wallets')}>
            {wallets.map((wallet, i) => (
              <Cryptocurrency key={i} wallet={wallet} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
